import { useDevice } from '../../../utils/hooks/useDevice';
import { useSafeViewport } from '../../../utils/hooks/useSafeViewport';
import {
	DESIGN_FRAME_HEIGHT,
	DESIGN_FRAME_WIDTH,
	MAX_SCALE,
	PANEL_WIDTH,
} from '../Achievements/achievementsTheme';
import { DESKTOP_FRAME, DESKTOP_SIDE, getDesktopScale } from '../Desktop/desktopLayout';

/**
 * The redesign modals are drawn on the 390-wide mobile frame and scaled once. On a phone that
 * scale is the viewport's share of the frame, capped at 1.15.
 *
 * Modals without a desktop artboard are dialogs drawn at 1.4x the desktop scale - a step under the
 * 1.62 the desktop top bar draws the mobile pieces at, so a dialog reads at desktop size without
 * filling the screen.
 */
const DESKTOP_MODAL_FACTOR = 1.4;

/**
 * The desktop artboards ("Edit Profile / Desktop — Screen States" 5605:102663 and the rest of
 * page "to desktop") draw each modal with "/ Desktop" twins of the mobile components at 1.25x on
 * a 1440x1024 frame, whose desktop scale is min(1440/1280, 1024/832) = 1.125. A modal following
 * those artboards therefore sits at 1.25 / 1.125 of the desktop scale.
 */
export const DESKTOP_TWIN_SCALE = 1.25;
export const DESKTOP_TWIN_FACTOR =
	DESKTOP_TWIN_SCALE / Math.min(1440 / DESKTOP_FRAME.width, 1024 / DESKTOP_FRAME.height);

type Options = {
	/**
	 * Design height the modal cannot give up, top offset and bottom gap included. Modals laid out
	 * absolutely against a fixed-height panel pass it so the whole panel always fits; modals whose
	 * body scrolls leave it out and simply get a shorter panel.
	 */
	fitHeight?: number;
	/**
	 * Overrides the desktop dialog factor for a modal that follows a desktop artboard, so it is
	 * drawn at the size that artboard gives it rather than the shared 1.4.
	 */
	desktopFactor?: number;
	/**
	 * The smallest scale `fitHeight` may shrink a desktop modal to. On a short screen, shrinking a
	 * tall panel until it fits would make its type unreadably small; under this floor the panel
	 * keeps a readable size and its body scrolls instead.
	 */
	minFitScale?: number;
	/** Design width of a panel wider than the shared 358 (a desktop-only layout). */
	panelWidth?: number;
	/**
	 * The gap the desktop window keeps around the panel, in the 1280 desktop frame's units;
	 * defaults to the content's 32 side margin.
	 */
	desktopMargin?: number;
};

export const useRedesignScale = ({
	fitHeight,
	desktopFactor = DESKTOP_MODAL_FACTOR,
	minFitScale = 0,
	panelWidth = PANEL_WIDTH,
	desktopMargin = DESKTOP_SIDE,
}: Options = {}) => {
	const { width, height } = useSafeViewport();
	const { isDesktop } = useDevice();

	if (!isDesktop) {
		return Math.min(width / DESIGN_FRAME_WIDTH, height / DESIGN_FRAME_HEIGHT, MAX_SCALE);
	}

	const desktopScale = getDesktopScale(width, height);
	const margin = desktopMargin * desktopScale;
	const target = desktopScale * desktopFactor;
	const widthFit = (width - margin * 2) / panelWidth;
	const heightFit = fitHeight
		? Math.max((height - margin * 2) / fitHeight, Math.min(minFitScale, target))
		: Infinity;

	return Math.min(target, widthFit, heightFit);
};

export default useRedesignScale;
